import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  query,
  where,
  writeBatch,
  Timestamp,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { Category } from "@/types/schema";
import { convertDoc } from "./utils";

const CATEGORIES_COLLECTION = "categories";
const PRODUCTS_COLLECTION = "products";

// Get all category names
export async function getCategories(): Promise<string[]> {
  try {
    const categoriesRef = collection(db, CATEGORIES_COLLECTION);
    const querySnapshot = await getDocs(categoriesRef);
    
    const categories = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return data.name as string;
    });
    
    return categories;
  } catch (error) {
    console.error("Error fetching categories:", error);
    throw new Error("Failed to fetch categories");
  }
}

// Get all category objects
export async function getCategoriesFromFirestore(): Promise<Category[]> {
  try {
    const categoriesRef = collection(db, CATEGORIES_COLLECTION);
    const querySnapshot = await getDocs(categoriesRef);
    
    return querySnapshot.docs.map(doc => convertDoc<Category>(doc));
  } catch (error) {
    console.error("Error fetching categories:", error);
    throw new Error("Failed to fetch categories");
  }
}

// Add a new category
export async function addCategory(
  categoryData: Omit<Category, "id" | "createdAt" | "updatedAt" | "productCount">
): Promise<Category> {
  try {
    const categoriesRef = collection(db, CATEGORIES_COLLECTION);
    
    // Check if category with same name exists
    const nameQuery = query(categoriesRef, where("name", "==", categoryData.name));
    const existingDocs = await getDocs(nameQuery);
    
    if (!existingDocs.empty) {
      throw new Error(`Category with name '${categoryData.name}' already exists`);
    }
    
    const docRef = await addDoc(categoriesRef, {
      ...categoryData,
      productCount: 0,
      createdAt: Timestamp.fromDate(new Date()),
      updatedAt: Timestamp.fromDate(new Date()),
    });
    
    return {
      id: docRef.id,
      ...categoryData,
      productCount: 0,
      createdAt: Timestamp.fromDate(new Date()),
      updatedAt: Timestamp.fromDate(new Date()),
    };
  } catch (error) {
    console.error("Error adding category:", error);
    throw new Error("Failed to add category: " + (error as Error).message);
  }
}

// Update a category
export async function updateCategory(
  categoryId: string,
  data: Partial<Omit<Category, "id" | "createdAt" | "updatedAt" | "productCount">>
): Promise<void> {
  try {
    const categoryRef = doc(db, CATEGORIES_COLLECTION, categoryId);
    
    // Check if category exists
    const categoryDoc = await getDoc(categoryRef);
    if (!categoryDoc.exists()) {
      throw new Error("Category not found");
    }
    
    await updateDoc(categoryRef, {
      ...data,
      updatedAt: Timestamp.fromDate(new Date()),
    });
  } catch (error) {
    console.error("Error updating category:", error);
    throw new Error("Failed to update category");
  }
}

// Delete a category
export async function deleteCategory(categoryId: string): Promise<void> {
  try {
    const categoryRef = doc(db, CATEGORIES_COLLECTION, categoryId);
    
    // Check if category exists
    const categoryDoc = await getDoc(categoryRef);
    if (!categoryDoc.exists()) {
      throw new Error("Category not found");
    }
    
    const categoryData = categoryDoc.data();
    
    // Check if products are using this category
    const productsRef = collection(db, PRODUCTS_COLLECTION);
    const productsQuery = query(productsRef, where("category", "==", categoryData.name));
    const productsSnapshot = await getDocs(productsQuery);
    
    if (!productsSnapshot.empty) {
      throw new Error(`Cannot delete category '${categoryData.name}' because it is used by ${productsSnapshot.size} products`);
    }
    
    // Delete the category
    await deleteDoc(categoryRef);
  } catch (error) {
    console.error("Error deleting category:", error);
    throw new Error("Failed to delete category: " + (error as Error).message);
  }
}

// Get a single category by ID
export async function getCategory(categoryId: string): Promise<Category | null> {
  try {
    const categoryRef = doc(db, CATEGORIES_COLLECTION, categoryId);
    const docSnapshot = await getDoc(categoryRef);
    
    if (!docSnapshot.exists()) {
      return null;
    }
    
    return convertDoc<Category>(docSnapshot);
  } catch (error) {
    console.error("Error fetching category:", error);
    throw new Error("Failed to fetch category");
  }
}

// Update product count for a category
export async function updateCategoryProductCount(categoryName: string): Promise<void> {
  try {
    // Get the category by name
    const categoriesRef = collection(db, CATEGORIES_COLLECTION);
    const q = query(categoriesRef, where("name", "==", categoryName));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return; // Category not found, nothing to update
    }
    
    const categoryDoc = querySnapshot.docs[0];
    
    // Count products in this category
    const productsRef = collection(db, PRODUCTS_COLLECTION);
    const productsQuery = query(productsRef, where("category", "==", categoryName));
    const productsSnapshot = await getDocs(productsQuery);
    
    // Update the category with the new count
    await updateDoc(categoryDoc.ref, {
      productCount: productsSnapshot.size,
      updatedAt: Timestamp.fromDate(new Date()),
    });
  } catch (error) {
    console.error(`Error updating product count for category ${categoryName}:`, error);
    throw new Error("Failed to update category product count");
  }
}

// Update counts for all categories
export async function updateAllCategoryCounts(): Promise<void> {
  try {
    // Get all categories
    const categoriesRef = collection(db, CATEGORIES_COLLECTION);
    const categoriesSnapshot = await getDocs(categoriesRef);
    
    if (categoriesSnapshot.empty) {
      return; // No categories found
    }
    
    // Get all products
    const productsRef = collection(db, PRODUCTS_COLLECTION);
    const productsSnapshot = await getDocs(productsRef);
    
    // Build a map of category counts
    const categoryCounts: { [categoryName: string]: number } = {};
    
    // Initialize all known categories with 0 count
    categoriesSnapshot.docs.forEach(doc => {
      const data = doc.data();
      categoryCounts[data.name] = 0;
    });
    
    // Count products for each category
    productsSnapshot.docs.forEach(doc => {
      const product = doc.data();
      const category = product.category;
      
      if (category && typeof category === 'string') {
        categoryCounts[category] = (categoryCounts[category] || 0) + 1;
      }
    });
    
    // Update all categories with their counts
    const batch = writeBatch(db);
    
    categoriesSnapshot.docs.forEach(doc => {
      const data = doc.data();
      const count = categoryCounts[data.name] || 0;
      
      batch.update(doc.ref, {
        productCount: count,
        updatedAt: Timestamp.fromDate(new Date())
      });
    });
    
    await batch.commit();
    
    console.log('Updated all category counts:', categoryCounts);
  } catch (error) {
    console.error('Error updating all category counts:', error);
    throw new Error('Failed to update category counts');
  }
}